import presenceReportHours from '../personas/presenceReportHours'
import halfDaysForHours from '../personas/halfDaysForHours'
import amsterdamDateKey from '../personas/amsterdamDateKey'
import toMillis from '../measurementSignals/toMillis'
import tariffForDate from './tariffForDate'
import { absenceCounts } from './resolveBillingRules'

/**
 * De rekenkern van de facturatie: aanwezigheid, tarieven en extra regels van
 * één periode → regels per deelnemer, plus alles wat níet te berekenen is.
 *
 * Geen Firestore, geen React: alles komt als argument binnen, zodat het scherm
 * en de export dezelfde uitkomst lezen. Spec: docs/features/facturatie-per-deelnemer.md.
 *
 * Twee dingen die bewust zo zijn:
 * - Niets verdwijnt stil. Een deelnemer zonder traject, een dag zonder tarief,
 *   een dubbele registratie: elk krijgt een eigen reden in `skipped`, met naam
 *   en dagen, en een eigen teller. Een overgeslagen dag die als € 0 in het
 *   totaal zou opgaan, laat een factuur compleet lijken terwijl er iets mist.
 * - Bedragen worden per regel op centen afgerond en in centen opgeteld.
 *
 * AI-CONTEXT: See docs/pitfalls.md#ui-aggregate-collapses-nothing-into-zero
 * AI-CONTEXT: See docs/pitfalls.md#dev-gate-counter-hides-its-own-policy
 * AI-CONTEXT: See docs/pitfalls.md#database-job-dedups-only-on-its-own-docid
 */

/**
 * @typedef {'noRoute'|'unitNotBillable'|'noTariff'|'duplicateReport'|'noHours'|'noDate'|'unknownPersona'} SkipReason
 */

/**
 * @typedef {object} BillingLine
 * @property {'presence'|'adjustment'} kind - Uit aanwezigheid of een extra regel
 * @property {string} personaId
 * @property {string} name
 * @property {string} routeTitle
 * @property {'halfday'|'day'|'hour'} unit
 * @property {number} [days] - Aantal meetellende dagen (alleen bij `presence`)
 * @property {string} [firstDate] - Eerste dag van deze regel, YYYY-MM-DD (alleen bij `presence`)
 * @property {string} [tariffStartDate] - Ingangsdatum van het tarief van de deelnemer; ontbreekt bij het tarief van het traject
 * @property {number} quantity - Aantal eenheden
 * @property {number} rate - Tarief per eenheid
 * @property {'persona'|'route'} tariffSource - Tarief van de deelnemer of van het traject
 * @property {number} amount - Bedrag in euro, op centen afgerond
 * @property {string} [adjustmentId] - Alleen bij `adjustment`
 * @property {string} [reason] - Alleen bij `adjustment`
 */

/**
 * @typedef {object} SkippedEntry
 * @property {string} personaId
 * @property {string} name - `''` als de deelnemer niet bekend is
 * @property {SkipReason} reason
 * @property {Array<string>} dates - De betrokken dagen (YYYY-MM-DD), oplopend
 * @property {string} detail - Bij `unitNotBillable` de eenheid; anders `''`
 * @property {Array<string>} adjustmentIds - Extra regels die hierdoor niet meetellen. Apart van
 *   `dates`: een extra regel is geen aanwezige dag.
 */

/**
 * @typedef {object} BillingResult
 * @property {Array<BillingLine>} lines
 * @property {Array<SkippedEntry>} skipped
 * @property {Partial<Record<SkipReason, number>>} skippedCounts - Aantal per reden
 * @property {number} skippedPersonaCount - Aantal verschillende deelnemers met iets in `skipped`
 * @property {Record<string, SkipReason>} skippedAdjustments - Per extra regel (ID) waarom hij niet meetelt
 * @property {number} total - Som van alle regels in euro
 */

const BILLABLE_UNITS = ['halfday', 'day', 'hour']

/**
 * Op centen afronden. Met `Number.EPSILON`, want 1.005 * 100 is in zwevende komma
 * 100.49999…, en dan werd € 1,005 € 1,00.
 * @param {number} value
 * @returns {number}
 */
const roundCents = (value) => Math.round((value + Number.EPSILON) * 100) / 100

/** @param {number} value @returns {number} Zonder ruis van zwevende komma */
const tidy = (value) => Math.round(value * 10000) / 10000

/** @param {object} report @returns {string} '' als er geen datum in zit */
const dateKeyOf = (report) => {
    const millis = toMillis(report?.Timestamp)
    return millis === null ? '' : amsterdamDateKey(new Date(millis))
}

/**
 * De uren die op één dag meetellen, of `null` als de dag niet meetelt.
 * Een meetellende afwezigheid telt met de geroosterde tijd.
 */
const countedHours = (report, rules) => {
    if (report.Type !== 'absent') return presenceReportHours(report).hours
    if (!absenceCounts(report.Reason, rules)) return null
    return presenceReportHours({ ...report, Type: 'correct', CorrectedStart: '', CorrectedEnd: '' }).hours
}

/** @returns {number} Eenheden voor één dag */
const unitsForDay = (unit, hours, rules) => {
    if (unit === 'halfday') return halfDaysForHours(hours, rules)
    if (unit === 'day') return 1
    return hours
}

/**
 * Waarom een extra regel voor deze deelnemer niet kan meetellen, of `null` als
 * het wel kan. Eén plek: de rekenkern gebruikt dit, en het formulier voor extra
 * regels ook, zodat de melding daar nooit iets anders zegt dan de factuur.
 *
 * @param {object} input
 * @param {{ID: string, RouteID?: string}|undefined} input.persona
 * @param {{Unit?: string, Tariff?: *}|undefined} input.route - Het traject van de deelnemer
 * @param {Array<object>} input.tariffs - `PersonaTariffs` van deze deelnemer
 * @param {string} input.periodEndKey - Laatste dag van de periode
 * @returns {{reason: SkipReason, detail: string}|null}
 */
const adjustmentBlocker = ({ persona, route, tariffs, periodEndKey }) => {
    if (!persona) return { reason: 'unknownPersona', detail: '' }
    if (!route) return { reason: 'noRoute', detail: '' }
    if (!BILLABLE_UNITS.includes(route.Unit)) return { reason: 'unitNotBillable', detail: route.Unit || '' }
    if (!tariffForDate(tariffs, periodEndKey, route.Tariff)) return { reason: 'noTariff', detail: '' }
    return null
}

/**
 * @param {object} input
 * @param {Array<object>} input.reports - `PersonaPresenceReports` van de periode
 * @param {Array<{ID: string, Name?: string, RouteID?: string}>} input.personas
 * @param {Array<{ID: string, Title?: string, Unit?: string, Tariff?: *}>} input.routes - `PersonaRoutes`
 * @param {Array<object>} input.tariffs - `PersonaTariffs` (alle deelnemers)
 * @param {Array<{ID: string, PersonaID: string, Quantity: number, Reason?: string, Deleted?: boolean}>} input.adjustments
 *   - `PersonaBillingAdjustments` van de periode
 * @param {import('./resolveBillingRules').BillingRules} input.rules - Uit `resolveBillingRules`
 * @param {string} input.periodEndKey - Laatste dag van de periode (YYYY-MM-DD); daarop
 *   wordt het tarief van een extra regel bepaald
 * @returns {BillingResult}
 */
const computeBilling = ({ reports, personas, routes, tariffs, adjustments, rules, periodEndKey }) => {
    if (!rules) throw new Error('computeBilling: geen rekenregels ingesteld')

    const personaById = new Map((personas || []).map((p) => [p.ID, p]))
    const routeById = new Map((routes || []).map((r) => [r.ID, r]))
    const tariffsByPersona = new Map()
    for (const tariff of tariffs || []) {
        if (!tariffsByPersona.has(tariff.PersonaID)) tariffsByPersona.set(tariff.PersonaID, [])
        tariffsByPersona.get(tariff.PersonaID).push(tariff)
    }

    /** @type {Map<string, SkippedEntry>} */
    const skippedByKey = new Map()
    /** @type {Record<string, SkipReason>} */
    const skippedAdjustments = {}
    const skip = (personaId, reason, date, detail = '', adjustmentId = '') => {
        const key = `${personaId}|${reason}`
        if (!skippedByKey.has(key)) {
            skippedByKey.set(key, { personaId, name: personaById.get(personaId)?.Name || '', reason, dates: [], detail, adjustmentIds: [] })
        }
        const entry = skippedByKey.get(key)
        if (date && !entry.dates.includes(date)) entry.dates.push(date)
        if (adjustmentId) {
            entry.adjustmentIds.push(adjustmentId)
            skippedAdjustments[adjustmentId] = reason
        }
    }

    /** Persona + route, of de reden waarom dat niet kan. */
    const contextFor = (personaId, date) => {
        const persona = personaById.get(personaId)
        if (!persona) return skip(personaId, 'unknownPersona', date)
        const route = routeById.get(persona.RouteID)
        if (!route) return skip(personaId, 'noRoute', date)
        if (!BILLABLE_UNITS.includes(route.Unit)) return skip(personaId, 'unitNotBillable', date, route.Unit || '')
        return { persona, route }
    }

    // Eén registratie per deelnemer per dag. Meer dan één is een oude dubbele
    // (inbox #327) en wordt nooit stil meegeteld.
    const reportsByDay = new Map()
    for (const report of reports || []) {
        if (!report || report.Deleted === true) continue
        const date = dateKeyOf(report)
        if (!date) { skip(report.PersonaID, 'noDate', ''); continue }
        const key = `${report.PersonaID}|${date}`
        if (!reportsByDay.has(key)) reportsByDay.set(key, [])
        reportsByDay.get(key).push({ report, date })
    }

    const days = [...reportsByDay.values()].sort((a, b) => (a[0].date < b[0].date ? -1 : a[0].date > b[0].date ? 1 : 0))

    /** @type {Map<string, BillingLine & {_cents: number, _quantity: number}>} */
    const presenceLines = new Map()

    for (const group of days) {
        const { report, date } = group[0]
        const personaId = report.PersonaID

        // Telt geen enkele registratie van deze dag mee (afwezig volgens de
        // rekenregels), dan valt er niets te factureren — ook niet als hij dubbel
        // staat. Daarna eerst de deelnemer zelf (traject, eenheid), dan pas de
        // dubbele registratie: 'geen traject' is het probleem dat je eerst oplost.
        const counted = group.map((entry) => countedHours(entry.report, rules)).filter((h) => h !== null)
        if (counted.length === 0) continue

        const context = contextFor(personaId, date)
        if (!context) continue
        if (group.length > 1) { skip(personaId, 'duplicateReport', date); continue }

        const hours = counted[0]
        if (hours <= 0) { skip(personaId, 'noHours', date); continue }

        const { persona, route } = context
        const tariff = tariffForDate(tariffsByPersona.get(personaId), date, route.Tariff)
        if (!tariff) { skip(personaId, 'noTariff', date); continue }

        // Ook de ingangsdatum in de sleutel: twee tarieven met hetzelfde bedrag
        // blijven twee regels, zodat 'tarief vanaf' de juiste datum noemt.
        const key = `${personaId}|${tariff.source}|${tariff.amount}|${tariff.startDate || ''}`
        if (!presenceLines.has(key)) {
            presenceLines.set(key, {
                kind: 'presence',
                personaId,
                name: persona.Name || '',
                routeTitle: route.Title || '',
                unit: route.Unit,
                days: 0,
                firstDate: date,
                ...(tariff.startDate ? { tariffStartDate: tariff.startDate } : {}),
                quantity: 0,
                rate: tariff.amount,
                tariffSource: tariff.source,
                amount: 0,
                _quantity: 0,
            })
        }
        const line = presenceLines.get(key)
        line.days += 1
        line._quantity += unitsForDay(route.Unit, hours, rules)
    }

    const lines = [...presenceLines.values()].map(({ _quantity, ...line }) => ({
        ...line,
        quantity: tidy(_quantity),
        amount: roundCents(_quantity * line.rate),
    }))

    for (const adjustment of adjustments || []) {
        if (!adjustment || adjustment.Deleted === true) continue
        const persona = personaById.get(adjustment.PersonaID)
        const route = persona ? routeById.get(persona.RouteID) : undefined
        const personaTariffs = tariffsByPersona.get(adjustment.PersonaID)
        const blocker = adjustmentBlocker({ persona, route, tariffs: personaTariffs, periodEndKey })
        if (blocker) { skip(adjustment.PersonaID, blocker.reason, '', blocker.detail, adjustment.ID); continue }

        const tariff = tariffForDate(personaTariffs, periodEndKey, route.Tariff)

        const quantity = Number(adjustment.Quantity) || 0
        lines.push({
            kind: 'adjustment',
            adjustmentId: adjustment.ID,
            personaId: adjustment.PersonaID,
            name: persona.Name || '',
            routeTitle: route.Title || '',
            unit: route.Unit,
            quantity,
            rate: tariff.amount,
            tariffSource: tariff.source,
            amount: roundCents(quantity * tariff.amount),
            reason: adjustment.Reason || '',
        })
    }

    const byName = (a, b) =>
        a.name.localeCompare(b.name, 'nl') || (a.personaId < b.personaId ? -1 : a.personaId > b.personaId ? 1 : 0)
    const kindOrder = { presence: 0, adjustment: 1 }
    // Binnen een deelnemer blijven aanwezigheidsregels op volgorde van hun eerste dag
    // (de Map is in datumvolgorde gevuld); `sort` is stabiel.
    lines.sort((a, b) => byName(a, b) || kindOrder[a.kind] - kindOrder[b.kind])

    const skipped = [...skippedByKey.values()]
        .map((entry) => ({ ...entry, dates: [...entry.dates].sort(), adjustmentIds: [...entry.adjustmentIds].sort() }))
        .sort((a, b) => byName(a, b) || (a.reason < b.reason ? -1 : a.reason > b.reason ? 1 : 0))

    const skippedCounts = {}
    for (const entry of skipped) skippedCounts[entry.reason] = (skippedCounts[entry.reason] || 0) + 1

    const totalCents = lines.reduce((sum, line) => sum + Math.round(line.amount * 100), 0)

    return {
        lines,
        skipped,
        skippedCounts,
        skippedPersonaCount: new Set(skipped.map((s) => s.personaId)).size,
        skippedAdjustments,
        total: totalCents / 100,
    }
}

export default computeBilling
export { adjustmentBlocker }
